// Tenantra Inc. - Slide-in tenant summary opened from Tenants and Reports tables
import { useEffect, useState } from 'react';
import { ArrowRight, Plus } from 'lucide-react';
import { navigate } from '@/services/navigation';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { DetailSheet, Field, Section, StatusText, PrimaryAction, SecondaryAction, fmtDate } from './DetailPanel';
import { TenantBilling } from './TenantBilling';
import { TenantMessages } from './TenantMessages';
import { EntityNotes } from './EntityNotes';

const PAY = {
  on_time: { label: 'On time', cls: 'text-emerald-600', dot: 'bg-emerald-500' },
  late: { label: 'Late', cls: 'text-amber-600', dot: 'bg-amber-500' },
  partial: { label: 'Partial', cls: 'text-amber-600', dot: 'bg-amber-500' },
  missed: { label: 'Missed', cls: 'text-red-600', dot: 'bg-red-500' },
};
const TENANCY = {
  active: { label: 'Active', cls: 'text-emerald-600', dot: 'bg-emerald-500' },
  pending_consent: { label: 'Pending consent', cls: 'text-amber-600', dot: 'bg-amber-500' },
  ended: { label: 'Ended', cls: 'text-slate-500', dot: 'bg-slate-400' },
};

const fmtMonth = (d) => {
  const [y, m] = d.split('-');
  return new Date(y, m - 1, 1).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' });
};

const rowNote = (r) => {
  const owing = parseFloat(r.payment?.amount_owing || 0);
  return [
    r.payment?.days_late > 0 && `${r.payment.days_late} days late`,
    owing > 0 && `$${owing.toLocaleString()} owing`,
    r.is_disputed && 'disputed',
  ].filter(Boolean).join(', ');
};

function RentHistory({ reports }) {
  if (!reports.length) return <p className="text-sm text-slate-500">No months reported yet.</p>;
  const onTime = reports.filter(r => r.payment?.status === 'on_time').length;
  return (
    <>
      <p className="text-sm text-slate-700 mb-3" data-testid="panel-ontime-summary">
        <span className="font-semibold text-slate-900">{onTime} of {reports.length}</span> months paid on time
      </p>
      <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="panel-rent-history">
        {reports.map((r) => (
          <li key={r.id} className="flex items-center justify-between px-3 py-2 text-sm">
            <span className="text-slate-700 w-24">{fmtMonth(r.report_period.start)}</span>
            <span className="flex-1 text-xs text-slate-400">{rowNote(r)}</span>
            <StatusText cfg={PAY[r.payment?.status] || PAY.on_time} />
          </li>
        ))}
      </ul>
    </>
  );
}

export function TenantPanel({ tenantId, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    setData(null);
    Promise.all([
      api.get(`/tenants/${tenantId}`),
      api.get(`/reports?tenant_id=${tenantId}&limit=12`).catch(() => ({ data: { items: [] } })),
      api.get(`/esign/tenants/${tenantId}/documents`).catch(() => ({ data: { documents: [] } })),
    ]).then(([t, r, d]) => setData({ tenant: t.data, reports: r.data.items || [], docs: d.data.documents || [] }))
      .catch((err) => { toast.error(apiHelpers.handleError(err)); onClose(); });
  }, [tenantId]);

  const tenant = data?.tenant;
  const tenancy = tenant?.tenancies?.find(t => t.status === 'active') || tenant?.tenancies?.[0];
  const consent = data?.docs.find(d => d.type === 'reporting');

  return (
    <DetailSheet
      open={!!tenantId} onClose={onClose} loading={!tenant} testId="tenant-panel"
      crumb="Tenants / Tenant record" title={tenant?.full_name}
      meta={tenant && <><span className="font-mono text-slate-500">{tenant.tenant_unique_id}</span><StatusText cfg={TENANCY[tenancy?.status] || TENANCY.ended} /></>}
      footer={tenant && <>
        <PrimaryAction onClick={() => navigate(`/landlord/tenants/${tenant.id}`)} testId="panel-open-full-record">Open full record <ArrowRight className="w-4 h-4" /></PrimaryAction>
        {tenancy?.id && <SecondaryAction onClick={() => navigate(`/landlord/reports/${tenancy.id}/new`)} testId="panel-add-report"><Plus className="w-4 h-4" /> Add report</SecondaryAction>}
      </>}
    >
      {tenant && <>
        <Section title="Tenancy">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Property" wide>{tenancy?.property_address || 'No tenancy'}</Field>
            <Field label="Monthly rent">{tenancy ? `$${parseFloat(tenancy.rent_amount || 0).toLocaleString()}` : 'Not set'}</Field>
            <Field label="Moved in">{fmtDate(tenancy?.move_in_date)}</Field>
            <Field label="Email">{tenant.email || 'Not provided'}</Field>
            <Field label="Phone">{tenant.phone || 'Not provided'}</Field>
          </dl>
        </Section>
        {tenancy?.id && <TenantBilling tenancyId={tenancy.id} />}
        {tenancy?.id && <TenantMessages tenancyId={tenancy.id} />}
        <EntityNotes type="tenant" id={tenant.id} />
        {tenancy?.household?.length > 0 && (
          <Section title="Household" aside={`${1 + tenancy.household.length} adults${tenancy.minors_count ? `, ${tenancy.minors_count} under 18` : ''}`}>
            <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="panel-household">
              <li className="px-3 py-2 flex justify-between text-sm"><span className="text-slate-800">{tenant.full_name}</span><span className="text-xs text-slate-500">Primary tenant · ID verified</span></li>
              {tenancy.household.map((h, i) => (
                <li key={i} className="px-3 py-2 flex justify-between gap-2 text-sm" data-testid={`panel-household-${i}`}><span className="text-slate-800">{h.full_name}{h.id_status === 'pending' && <button onClick={() => api.post(`/onboarding/tenancies/${tenancy.id}/roommates/${i}/resend`).then(r => (r.data.email_sent ? toast.success(`ID check link sent to ${h.full_name}`) : toast.warning('Email could not be sent. Try again later.'))).catch(e => toast.error(apiHelpers.handleError(e)))} className="block text-[11px] font-medium text-teal-700 hover:text-teal-800" data-testid={`panel-household-${i}-resend`}>Resend ID link</button>}</span>
                  <span className={`text-xs ${h.id_status === 'pending' ? 'text-amber-700' : h.id_status === 'verified' ? 'text-emerald-700' : 'text-slate-500'}`}>{h.relationship === 'roommate' ? `Roommate · ${h.id_status === 'verified' ? 'ID verified' : h.id_status === 'pending' ? 'ID pending' : 'ID not verified'}` : 'Family / known'}</span></li>
              ))}
            </ul>
          </Section>
        )}
        <Section title="Reporting consent">
          <p className="text-sm text-slate-800" data-testid="panel-consent">
            {tenancy?.source === 'onboarding' && tenancy.consent_approved_at ? <><span className="text-emerald-600 font-medium">Given during onboarding</span> on {fmtDate(tenancy.consent_approved_at)}</>
              : consent?.status === 'signed'
              ? <><span className="text-emerald-600 font-medium">Signed</span> on {fmtDate(consent.signed_at)}</>
              : consent ? <span className="text-amber-600 font-medium">Awaiting signature</span>
              : <span className="text-slate-500">No consent document yet</span>}
          </p>
        </Section>
        <Section title="Rent history" aside={data.reports.length ? `Last ${data.reports.length} months` : null}>
          <RentHistory reports={data.reports} />
        </Section>
        {tenant.extra_info && Object.keys(tenant.extra_info).length > 0 && (
          <Section title="Imported details" aside={`${Object.keys(tenant.extra_info).length} fields`}>
            <dl className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="panel-imported-details">
              {Object.entries(tenant.extra_info).map(([k, v]) => (
                <div key={k} className="px-3 py-2 flex justify-between gap-3 text-sm" data-testid={`panel-extra-${k}`}>
                  <dt className="text-slate-500 truncate">{k}</dt>
                  <dd className="text-slate-800 text-right truncate">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </Section>
        )}
      </>}
    </DetailSheet>
  );
}
